import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import { StreamingHelperService } from "../../../services/streaming-helper-service";
import { BOARD_CLIENT_ID } from "./board-client-id";
import { BoardService } from "./board-service";
import { BoardChangeEvent } from "./types";

// Multi-user live sync: a single Server-Sent Events stream delivers board change events that
// OTHER tabs in this workspace produced. We feed each one into BoardService.applyRemote, which
// drives the exact same scoped invalidation the in-process signal uses — so a teammate's create/
// move/edit reconciles the right column or detail without a full reload, and without any new
// per-view wiring.
//
// We use the fetch-based StreamingHelperService (NOT native EventSource, which can't send the
// designer's Bearer auth header), mirror the notifications stream's reconnect loop, and pass our
// per-tab clientId so the server never echoes this tab's own changes back to it.

const RECONNECT_DELAY_MS: number = 3000;
let streaming: boolean = false;
let streamAbort: AbortController | null = null;

function isBoardChangeEvent(data: unknown): data is BoardChangeEvent {
  return typeof data === "object" && data !== null && typeof (data as { kind?: unknown }).kind === "string";
}

function onStreamEvent(data: unknown): void {
  if (isBoardChangeEvent(data)) {
    BoardService.applyRemote(data);
  }
}

async function runEventStream(): Promise<void> {
  const url: string = `${CommonHelperFunctions.GetBackendUrl()}/board/events/stream?clientId=${encodeURIComponent(BOARD_CLIENT_ID)}`;
  while (streaming) {
    const controller: AbortController = new AbortController();
    streamAbort = controller;
    try {
      await StreamingHelperService.streamEvents(url, onStreamEvent, controller.signal);
    } catch {
      // network/stream error — fall through to the reconnect delay
    }
    if (!streaming) break;
    await new Promise<void>((resolve: () => void) => setTimeout(resolve, RECONNECT_DELAY_MS));
  }
}

// Idempotent — safe to call from every consumer's mount; there is no polling.
export function connectBoardEventStream(): void {
  if (streaming) return;
  if (typeof fetch === "undefined") return; // SSR / unsupported environment
  streaming = true;
  void runEventStream();
}

export function disconnectBoardEventStream(): void {
  streaming = false;
  if (streamAbort) {
    streamAbort.abort();
    streamAbort = null;
  }
}
